import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Heart, Play, Sparkles } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import rabbiImage from "@/assets/rabbi-teaching.jpg";
const rabbiWithRebbeImage = "/rabbi-with-rebbe.webp";
import chabadBuilding from "@/assets/chabad-building.jpg";

export const Hero = () => {
  const { t } = useTranslation();
  
  const scrollToDonate = () => {
    document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToVideo = () => {
    document.getElementById('video')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen lg:min-h-[90vh] overflow-hidden" style={{ background: 'var(--gradient-hero)' }}>
      {/* Hexagonal Pattern Overlay */}
      <div className="hex-pattern absolute inset-0 pointer-events-none" />

      {/* Subtle Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/8 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/8 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      </div>
      
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Premium Header */}
        <header className="flex items-center justify-between py-5 lg:py-6 animate-fade-in">
          <div className="flex items-center gap-3 bg-primary-foreground/10 backdrop-blur-md rounded-full px-4 py-2 border border-primary-foreground/10 shadow-lg">
            <img 
              src={chabadBuilding} 
              alt="Chabad Flushing - Chabad of Flushing Jewish Community Center Queens NY" 
              className="h-9 w-9 lg:h-10 lg:w-10 object-cover rounded-full ring-2 ring-secondary/30"
            />
            <span className="text-primary-foreground font-technical text-sm lg:text-base hidden sm:block tracking-wide uppercase">
              Chabad Flushing
            </span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSelector />
            <Button
              onClick={scrollToDonate}
              size="sm"
              className="btn-gold text-secondary-foreground font-semibold px-4 lg:px-5 h-9 lg:h-10 rounded-full shadow-warm transition-all hover:shadow-lg hover:scale-[1.02]"
            >
              <Heart className="w-4 h-4 ltr:mr-1.5 rtl:ml-1.5" />
              <span className="hidden sm:inline">{t('hero.donateNow')}</span>
              <span className="sm:hidden">{t('common.donate')}</span>
            </Button>
          </div>
        </header>

        {/* Hero Content */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-10 lg:py-16 xl:py-20">
          {/* Left Column - Text Content */}
          <div className="space-y-6 lg:space-y-8 text-center lg:ltr:text-left lg:rtl:text-right animate-fade-in">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-primary-foreground/10 backdrop-blur-sm rounded-full px-4 py-2 border border-primary-foreground/10">
              <div className="relative">
                <Sparkles className="w-4 h-4 text-secondary" />
                <div className="absolute inset-0 animate-ping">
                  <Sparkles className="w-4 h-4 text-secondary/50" />
                </div>
              </div>
              <span className="text-primary-foreground/90 font-technical text-xs uppercase tracking-[0.2em]">
                {t('hero.badge')}
              </span>
            </div>

            {/* Main Headline */}
            <div className="space-y-2 lg:space-y-3">
              <h1 className="font-display font-normal text-primary-foreground leading-[1.1] tracking-wide text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl">
                Chabad Flushing - Donate to Chabad of Flushing
              </h1>
              <p className="block text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl mt-1 bg-gradient-to-r from-secondary via-warm to-secondary bg-clip-text text-transparent font-display font-normal tracking-wide" aria-hidden="true">
                {t('hero.headline2')}
              </p>
              <p className="block text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl mt-1 text-primary-foreground/85 font-display font-normal tracking-wide" aria-hidden="true">
                {t('hero.headline3')}
              </p>
            </div>

            {/* Gold Architectural Divider */}
            <div className="gold-divider max-w-xs mx-auto lg:mx-0" />

            {/* Description */}
            <p className="text-lg sm:text-xl lg:text-xl text-primary-foreground/75 leading-relaxed max-w-xl mx-auto lg:mx-0 font-sans">
              {t('hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start rtl:lg:justify-end">
              <Button
                onClick={scrollToDonate}
                size="lg"
                className="btn-gold text-secondary-foreground text-base lg:text-lg h-14 px-7 lg:px-8 font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] group"
              >
                <Heart className="w-5 h-5 ltr:mr-2 rtl:ml-2 transition-transform group-hover:scale-110" />
                {t('hero.donateNow')}
              </Button>
              
              <Button
                onClick={scrollToVideo}
                size="lg"
                variant="outline"
                className="bg-primary-foreground/5 hover:bg-primary-foreground/10 border border-secondary/30 text-primary-foreground backdrop-blur-sm text-base lg:text-lg h-14 px-7 lg:px-8 font-semibold rounded-full transition-all duration-300 hover:border-secondary/50"
              >
                <Play className="w-5 h-5 ltr:mr-2 rtl:ml-2 fill-current" />
                {t('hero.learnMore')}
              </Button>
            </div>
          </div>

          {/* Right Column - Images */}
          <div className="relative animate-fade-in-delay">
            <div className="relative flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-5">
              {/* First Image */}
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-br from-secondary/30 to-accent/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
                <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
                  <img
                    src={rabbiWithRebbeImage}
                    alt="Chabad Flushing Rabbi with the Lubavitcher Rebbe - Chabad of Flushing leadership"
                    loading="eager"
                    decoding="async"
                    width={600}
                    height={750}
                    className="w-full aspect-[4/5] object-cover object-top transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent" />
                </div>
              </div>

              {/* Second Image */}
              <div className="relative group lg:mt-12">
                <div className="absolute -inset-2 bg-gradient-to-br from-warm/30 to-secondary/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-70 transition-opacity" />
                <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
                  <img
                    src={rabbiImage}
                    alt="Rabbi Shraga teaching Torah at Chabad Flushing - Jewish education Queens NY"
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={750}
                    className="w-full aspect-[4/5] object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/20 to-transparent" />
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 hidden lg:block animate-float">
              <div className="bg-card rounded-full px-5 py-3 shadow-card border border-border">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-secondary"></span>
                  </span>
                  <span className="text-sm font-technical font-medium text-foreground tracking-wide">{t('common.donationsOpen')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gold Architectural Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="gold-divider" />
      </div>
    </section>
  );
};
